import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Download, Check, HandHeart, Trophy, Sparkles, Users } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES, SPONSORSHIP_GUIDE_URL, SPONSOR_TIERS } from "@/lib/content";
import { Seo } from "@/components/Seo";
import { Reveal, Eyebrow, PrimaryButton, GhostButton, PageHero } from "@/components/site/ui";

const WHY = [
  { icon: Trophy, title: "Proven & Rising", text: "2026 MVC Champion and NCAA qualifier now competing professionally on the Continental Tour." },
  { icon: Users, title: "Engaged Audience", text: "A growing, values-driven community across the Fab Lab, Instagram, and TikTok." },
  { icon: Sparkles, title: "Authentic Storytelling", text: "Elite competitor, proven relay contributor, and emerging lifestyle brand, all in one." },
];

function LogoTile({ s, size = "md", linked }) {
  const h = size === "lg" ? "h-24" : size === "sm" ? "h-20" : "h-24";
  const inner = (
    <div className={`flex ${h} items-center justify-center rounded-2xl bg-white border border-champagne/30 p-6 transition-all hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(184,122,91,0.15)]`}>
      {s.logo_url ? <img src={s.logo_url} alt={s.name} className="max-h-16 max-w-full object-contain" /> : <span className="font-heading text-lg text-ink text-center">{s.name}</span>}
    </div>
  );
  return linked && s.website
    ? <a href={s.website} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-tile-${s.id}`} title={s.name}>{inner}</a>
    : <div data-testid={`sponsor-tile-${s.id}`} title={s.name}>{inner}</div>;
}

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/sponsors").then(({ data }) => setSponsors(data)).catch(() => {});
  }, []);

  const featured = sponsors.filter((s) => s.tier === "featured");
  const supporting = sponsors.filter((s) => s.tier === "supporting");
  const community = sponsors.filter((s) => s.tier === "community");

  return (
    <div data-testid="sponsors-page" className="bg-ivory">
      <Seo
        title="Sponsors & Partners | Leah Thames"
        description="Partner with professional sprinter Leah Thames. Explore sponsorship tiers, current partners, and how your brand can be part of the journey. Download the sponsorship guide."
        path="/sponsors"
      />
      <PageHero
        eyebrow="Sponsors & Partners"
        title="The People Powering The Dream"
        lines="Behind every race is a team that believes."
        sub="From training and travel to competition and recovery, these partners make it possible for Leah to chase her goals and represent her community at the highest level."
        image={IMAGES.podiumMVP}
      >
        <Reveal delay={0.24}>
          <div className="mt-9 flex flex-wrap gap-3">
            <PrimaryButton href={SPONSORSHIP_GUIDE_URL} testid="sponsors-hero-guide-btn"><Download size={16} /> Download Sponsorship Guide</PrimaryButton>
            <GhostButton to="/#contact" testid="sponsors-hero-contact-btn">Become a Sponsor</GhostButton>
          </div>
        </Reveal>
      </PageHero>

      {/* Featured / Signature partners */}
      {featured.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal><Eyebrow>Signature Partners</Eyebrow><h2 className="mt-3 font-heading text-3xl md:text-5xl text-ink">Featured Sponsors</h2></Reveal>
            <div className="mt-10 grid md:grid-cols-2 gap-6">
              {featured.map((s) => (
                <Reveal key={s.id}>
                  <div data-testid={`featured-sponsor-${s.id}`} className="h-full rounded-[2rem] bg-white border border-champagne/30 p-8 md:p-10 flex flex-col shadow-[0_20px_50px_rgba(184,122,91,0.10)]">
                    <div className="flex h-24 items-center justify-start">
                      {s.logo_url ? <img src={s.logo_url} alt={s.name} className="max-h-20 max-w-[70%] object-contain object-left" /> : <span className="font-heading text-2xl text-ink">{s.name}</span>}
                    </div>
                    <h3 className="mt-6 font-heading text-2xl text-ink">{s.name}</h3>
                    {s.blurb && <p className="mt-3 font-body text-sm md:text-base text-ink/70 leading-relaxed flex-1">{s.blurb}</p>}
                    {s.website && (
                      <a href={s.website} target="_blank" rel="noopener noreferrer" data-testid={`featured-sponsor-link-${s.id}`} className="mt-6 inline-flex items-center gap-2 font-body text-sm uppercase tracking-wide text-bronze hover:text-ink transition-colors">
                        Visit Website <ExternalLink size={15} />
                      </a>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Supporting sponsors */}
      {supporting.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal><Eyebrow>Supporting Sponsors</Eyebrow></Reveal>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
              {supporting.map((s) => <Reveal key={s.id}><LogoTile s={s} linked /></Reveal>)}
            </div>
          </div>
        </section>
      )}

      {/* Community supporters (no links) */}
      {community.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal><Eyebrow>Community Supporters</Eyebrow></Reveal>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
              {community.map((s) => <Reveal key={s.id}><LogoTile s={s} size="sm" /></Reveal>)}
            </div>
          </div>
        </section>
      )}

      {/* Why partner */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-ivory to-blush/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal><Eyebrow>Why Partner</Eyebrow><h2 className="mt-3 font-heading text-3xl md:text-5xl text-ink">More Than A Logo Placement</h2></Reveal>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div className="h-full rounded-[2rem] bg-white border border-champagne/30 p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blush/25 text-bronze"><w.icon size={22} /></span>
                  <h3 className="mt-5 font-heading text-xl text-ink">{w.title}</h3>
                  <p className="mt-3 font-body text-sm text-ink/70 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship tiers */}
      <section id="tiers" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal><Eyebrow>Get Involved</Eyebrow><h2 className="mt-3 font-heading text-3xl md:text-5xl text-ink">Ways To Sponsor</h2></Reveal>
            <Reveal delay={0.05}><p className="mt-5 font-accent text-xl md:text-2xl italic text-bronze">Find the partnership that fits your business.</p></Reveal>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6 items-stretch">
            {SPONSOR_TIERS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <div data-testid={`tier-card-${i}`} className={`h-full rounded-[2rem] p-8 flex flex-col border ${t.highlight ? "bg-ink text-ivory border-ink shadow-[0_24px_60px_rgba(43,43,43,0.25)]" : "bg-white text-ink border-champagne/30"}`}>
                  <span className={`self-start rounded-full px-3 py-1 text-[0.65rem] uppercase tracking-wide ${t.highlight ? "bg-blush text-ink" : "bg-blush/25 text-bronze"}`}>{t.tag}</span>
                  <h3 className={`mt-4 font-heading text-2xl ${t.highlight ? "text-ivory" : "text-ink"}`}>{t.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className={`font-heading text-3xl ${t.highlight ? "text-ivory" : "text-ink"}`}>{t.price}</span>
                    {t.period && <span className={`font-body text-sm ${t.highlight ? "text-ivory/70" : "text-ink/55"}`}>{t.period}</span>}
                  </div>
                  <p className={`mt-2 font-body text-sm leading-relaxed ${t.highlight ? "text-ivory/75" : "text-ink/65"}`}>{t.desc}</p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {t.perks.map((p) => (
                      <li key={p} className={`flex gap-3 font-body text-sm ${t.highlight ? "text-ivory/85" : "text-ink/75"}`}>
                        <Check size={16} className={`shrink-0 mt-0.5 ${t.highlight ? "text-blush" : "text-bronze"}`} /> {p}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    {t.highlight
                      ? <PrimaryButton to="/#contact" testid={`tier-cta-${i}`} className="w-full">Get In Touch</PrimaryButton>
                      : <GhostButton to="/#contact" testid={`tier-cta-${i}`} className="w-full">Get In Touch</GhostButton>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2.5rem] bg-ink text-ivory p-10 md:p-16 text-center">
              <HandHeart className="mx-auto text-blush" size={30} />
              <h2 className="mt-5 font-heading text-3xl md:text-5xl leading-tight">Be Part of My Journey</h2>
              <p className="mt-5 font-body text-sm md:text-base text-ivory/75 max-w-2xl mx-auto leading-relaxed">If you're looking for a meaningful way to support a local athlete while gaining visibility for your business, I'd love to connect with you.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <PrimaryButton href={SPONSORSHIP_GUIDE_URL} testid="sponsors-cta-guide-btn" className="w-full sm:w-auto"><Download size={16} /> Download Sponsorship Guide</PrimaryButton>
                <GhostButton to="/#contact" testid="sponsors-cta-contact-btn" className="w-full sm:w-auto !text-ivory !border-ivory/40 hover:!bg-ivory/10">Contact Leah</GhostButton>
              </div>
              <Link to="/" data-testid="sponsors-back-home" className="mt-8 inline-flex items-center gap-2 font-body text-xs uppercase tracking-wide text-ivory/60 hover:text-blush transition-colors">
                <ArrowUpRight size={14} className="rotate-[-135deg]" /> Back to Home
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
